/*
 * Copyright © 2023 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useContext, useEffect, useState } from "react"
import { Box } from "@mui/material"
import Target from "@medaica/common/components/icons/target"
import VirtualExamContext from "views/exam/virtual-exam/virtual-exam-context"
import { observer } from "mobx-react-lite"

const targetHeight = 51
const targetWidth = 51

/**
 * The crosshair the healthcare provider moves over the patient's picture. The provider sends its position as fractions
 * of the picture's width and height, so it lands on the same point of the picture whatever size each of them sees it
 * at.
 */
const TargetOverlay = observer((): ReactElement => {
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const { avStore } = useContext(VirtualExamContext)

  useEffect(() => {
    const dataTrack = avStore.remoteDataTrack
    if (!dataTrack) {
      return
    }
    const handleMessage = (message: string | ArrayBuffer) => {
      const payload = JSON.parse(message as string)
      if (payload.type === "targetPos") {
        const [x, y] = payload.detail as [number, number]
        // The patient sees their own picture mirrored, so the x position is flipped
        setPosition({ x: 1 - x, y })
      }
    }
    dataTrack.on("message", handleMessage)
    return () => {
      dataTrack.off("message", handleMessage)
    }
  }, [avStore.remoteDataTrack])

  const left = `${position.x * 100}%`
  const top = `${position.y * 100}%`

  return (
    <Box sx={{ position: "absolute", height: "100%", width: "100%" }}>
      <Box
        data-testid="target"
        sx={{
          height: `${targetHeight}px`,
          width: `${targetWidth}px`,
          position: "absolute",
          left: `calc(${left} - ${targetWidth / 2}px)`,
          top: `calc(${top} - ${targetHeight / 2}px)`,
          touchAction: "none",
          borderRadius: "50%",
          zIndex: 100,
        }}
      >
        <Target />
      </Box>
      <Box
        sx={{
          position: "absolute",
          backgroundColor: "white",
          height: "1px",
          width: "100%",
          top: `calc(${top} - 0.5px)`,
          left: 0,
          opacity: 0.4,
          zIndex: 10,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          backgroundColor: "white",
          height: "100%",
          width: "1px",
          top: 0,
          left: `calc(${left} - 0.5px)`,
          opacity: 0.4,
          zIndex: 10,
        }}
      />
    </Box>
  )
})

export default TargetOverlay
